import { Button, Code, Group, Stack, Text, TextInput } from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { IconSearch, IconUpload } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import {
  EmptyState,
  InlineError,
  LoadingSkeleton,
  RecordCard,
  SectionCard,
  StateBadge,
} from "../../components/ui";
import { ApiClientError, api, type Customer } from "../../lib/api";
import { Page } from "../../shell/Page";

/**
 * Store Manager's customer master (Q6).
 *
 * The list every other role selects from, maintained here by hand or by CSV
 * upload. The upload validates the whole file before writing anything — a
 * half-imported list is worse than a rejected one, because you cannot tell
 * which half is missing without comparing both files line by line.
 */
export function CustomerAdmin() {
  const [customers, setCustomers] = useState<Customer[]>();
  const [query, setQuery] = useState("");
  const [error, setError] = useState<string>();
  const [rowErrors, setRowErrors] = useState<{ line: number; message: string }[]>();
  const [busy, setBusy] = useState(false);

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const fileRef = useRef<HTMLInputElement | null>(null);

  async function refresh() {
    setError(undefined);
    try {
      const { customers: loaded } = await api.customers(query.trim() || undefined, true);
      setCustomers(loaded);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not load customers.");
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function addCustomer(event: React.FormEvent) {
    event.preventDefault();
    setError(undefined);
    setBusy(true);
    try {
      const { customer } = await api.createCustomer({
        name: name.trim(),
        phone: phone.trim(),
        ...(address.trim() ? { address: address.trim() } : {}),
      });
      notifications.show({
        color: "teal",
        message: `Added ${customer.name} (${customer.phone}).`,
      });
      setName("");
      setPhone("");
      setAddress("");
      await refresh();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not add that customer.");
    } finally {
      setBusy(false);
    }
  }

  async function upload(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setError(undefined);
    setRowErrors(undefined);
    setBusy(true);
    try {
      const csv = await file.text();
      const result = await api.bulkUploadCustomers(csv);
      notifications.show({
        color: "teal",
        message:
          `Imported ${result.created} customer${result.created === 1 ? "" : "s"}` +
          (result.skippedExisting > 0
            ? `. ${result.skippedExisting} already on file were left unchanged.`
            : "."),
      });
      await refresh();
    } catch (caught) {
      if (caught instanceof ApiClientError) {
        setError(caught.message);
        if (Array.isArray(caught.details)) {
          setRowErrors(caught.details as { line: number; message: string }[]);
        }
      } else {
        setError("Could not read that file.");
      }
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive(customer: Customer) {
    setError(undefined);
    try {
      await api.updateCustomer(customer.id, { isActive: !customer.isActive });
      await refresh();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not update that customer.");
    }
  }

  /**
   * Deactivating is not a delete - the customer stays, with their whole
   * history - but it takes them out of every picker in the app, so somebody
   * looking for them tomorrow will not find them. Worth saying which way it
   * goes before it happens.
   */
  function confirmToggle(customer: Customer) {
    modals.openConfirmModal({
      title: customer.isActive ? `Deactivate ${customer.name}` : `Reactivate ${customer.name}`,
      children: (
        <Text fz="md">
          {customer.isActive
            ? "They will stop appearing in the customer pickers, so no new day order, service job or collection can be raised against them. Nothing already recorded changes."
            : "They will appear in the customer pickers again."}
        </Text>
      ),
      labels: {
        confirm: customer.isActive ? "Deactivate" : "Reactivate",
        cancel: "Cancel",
      },
      ...(customer.isActive ? { confirmProps: { color: "red" } } : {}),
      onConfirm: () => void toggleActive(customer),
    });
  }

  return (
    <Page
      title="Customers"
      subtitle="The master list every module selects from"
      width="wide"
    >
      {error && <InlineError>{error}</InlineError>}

      {rowErrors && rowErrors.length > 0 && (
        <SectionCard title="Rows to fix">
          <Stack gap="sm">
            <Text fz="md" c="gray.7">
              Nothing was imported. Correct these and upload again.
            </Text>
            <Stack gap={4}>
              {rowErrors.map((row) => (
                <Text key={row.line} fz="sm" c="red.8">
                  <strong>Line {row.line}</strong> — {row.message}
                </Text>
              ))}
            </Stack>
          </Stack>
        </SectionCard>
      )}

      <SectionCard title="Add a customer">
        <form onSubmit={addCustomer}>
          <Stack gap="sm">
            <TextInput
              id="c-name"
              label="Name"
              withAsterisk
              value={name}
              onChange={(event) => setName(event.currentTarget.value)}
              required
              minLength={2}
              maxLength={200}
            />
            <TextInput
              id="c-phone"
              label="Phone"
              withAsterisk
              type="tel"
              inputMode="numeric"
              className="num"
              value={phone}
              onChange={(event) => setPhone(event.currentTarget.value)}
              placeholder="10-digit mobile number"
              required
              description="Phone is the customer's identity in the system — it must be unique."
            />
            <TextInput
              id="c-address"
              label="Address"
              description="Optional."
              value={address}
              onChange={(event) => setAddress(event.currentTarget.value)}
              maxLength={500}
            />
            <Button type="submit" size="md" fullWidth loading={busy}>
              Add customer
            </Button>
          </Stack>
        </form>
      </SectionCard>

      <SectionCard title="Bulk upload">
        <Stack gap="sm">
          <Text fz="md" c="gray.7">
            A CSV with a header row containing <Code>name</Code> and{" "}
            <Code>phone</Code> (and optionally <Code>address</Code>). Customers
            already on file are skipped, never overwritten.
          </Text>
          <Button
            variant="default"
            size="md"
            fullWidth
            leftSection={<IconUpload size={18} stroke={1.8} />}
            onClick={() => fileRef.current?.click()}
            loading={busy}
          >
            Choose CSV file
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            className="visually-hidden"
            onChange={upload}
          />
        </Stack>
      </SectionCard>

      <SectionCard title="All customers">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void refresh();
          }}
        >
          <Group gap="sm" align="flex-end" wrap="nowrap">
            <TextInput
              type="search"
              value={query}
              onChange={(event) => setQuery(event.currentTarget.value)}
              placeholder="Search by phone or name"
              aria-label="Search customers"
              leftSection={<IconSearch size={16} stroke={1.8} />}
              style={{ flex: 1 }}
            />
            <Button type="submit" variant="default">
              Find
            </Button>
          </Group>
        </form>
      </SectionCard>

      {!customers && !error && <LoadingSkeleton variant="list" count={5} />}
      {customers?.length === 0 && <EmptyState title="No customers yet" />}

      {customers?.map((customer) => (
        <RecordCard
          key={customer.id}
          primary={customer.name}
          secondary={customer.phone ?? undefined}
          meta={customer.address ?? undefined}
          badges={
            /* A word, not a greyed-out row: "inactive" is the fact, and a row
               that is merely faint is a row people read as normal. */
            customer.isActive ? undefined : (
              <StateBadge state="svcCancelled" label="Inactive" />
            )
          }
          action={
            <Button variant="default" size="sm" onClick={() => confirmToggle(customer)}>
              {customer.isActive ? "Deactivate" : "Reactivate"}
            </Button>
          }
        />
      ))}
    </Page>
  );
}
